import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from 'vue';
import { defineMessages } from '@/lib/i18n';

import { store } from '@/store/index';
import { readAssertedUserProfile } from '@/store/main/getters';
import { User } from '@/interfaces/vuex/user';
import TextTruncatedChip from '@/components/TextTruncatedChip.vue';
import { formatToDate, formatToDateTime } from '@/utils/datetime';
import { RoleId } from '@/lib/enum';
import { usePermissionComposable } from '@/composables/usePermission';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import { useDepartmentComposable } from '@/composables/useDepartment';
import { buildDepartmentPathMap } from '@/utils/departmentTree';
import {
  Department,
  DepartmentTreeNode,
} from '@/interfaces/model/general/department';
import UserUpdateDialog from '@/views/pages/companyUsers/UserUpdateDialog.vue';
import UserDeleteDialog from '@/views/pages/companyUsers/UserDeleteDialog.vue';
import UserSendActivationDialog from '@/views/pages/companyUsers/UserSendActivationDialog.vue';
import UserPasswordResetDialog from '@/views/pages/companyUsers/UserPasswordResetDialog.vue';
import UserDeactivateDialog from '@/views/pages/companyUsers/UserDeactivateDialog.vue';
import { useUserComposable } from '@/composables/useUser';
import sharedMessages from '@/lib/shared-messages';
import { i18n } from '@/plugins/vue-i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'CompanyUsersTable',
  props: {
    users: null,
    selectableDepartments: null,
    departmentTree: null
  },
  emits: ["updateUser", "deleted", "updated"],
  setup(__props: any, { emit: emits }: { emit: ({
  (
    e: 'updateUser',
    data: { userId: string; departments: Department[]; roleId: number }
  ): void;
  (e: 'deleted'): void;
  (e: 'updated'): void;
}), expose: any, slots: any, attrs: any }) {

const $t = i18n.t.bind(i18n);

const m = defineMessages({
  activatedUser: 'アクティベート済みのユーザー',
  activationNeeded: 'アカウント登録完了が必要です',
  resendActivationEmail: 'アカウント有効化メールを送信',
  deleteUser: 'このユーザーを削除',
  editingUserInfo: 'ユーザー情報の編集',
  set: '設定済み',
  notSet: '未設定',
  moreOptions: 'その他のオプション',
  deactivateAccount: 'アカウントの無効化',
  deleteAccount: 'アカウントの削除',
  scimDeleteRequiresDeprovision:
    'IdP側でこちらのユーザーを除外した場合にのみ、ユーザーを削除できるようになります',
});





const highlightRow = ref('');
const openMenuUserEmail = ref('');
const isUserDeleteDialogVisible = ref(false);
const isUserUpdateDialogVisible = ref(false);
const isUserSendActivationDialogVisible = ref(false);
const isPasswordResetDialogVisible = ref(false);
const isUserDeactivateDialogVisible = ref(false);
const targetUser = ref<User | null>(null);

const { deleteCompanyUser, deactivateCompanyUser, regenerateActivationMail } =
  useUserComposable();
const { hasPermission, PERMISSIONS: P } = usePermissionComposable();
const { companyDepartments } = useDepartmentComposable();
const departmentPathByUuid = computed(() =>
  buildDepartmentPathMap(companyDepartments.value)
);

// Caps each row's department chips so a user with many departments doesn't
// balloon the row height to 10+ lines and bury the hover action buttons.
const MAX_VISIBLE_DEPARTMENTS = 3;
const visibleDepartments = (user: User) =>
  user.departments.slice(0, MAX_VISIBLE_DEPARTMENTS);
const hiddenDepartments = (user: User) =>
  user.departments.slice(MAX_VISIBLE_DEPARTMENTS);
const hiddenDepartmentCount = (user: User) =>
  Math.max(0, user.departments.length - MAX_VISIBLE_DEPARTMENTS);
const { COMPANY_FEATURES, isFeatureActive, isScimEnabled } =
  useCompanyFeatureComposable();
const isSamlLoginEnabled = computed(() =>
  isFeatureActive(COMPANY_FEATURES.MANAGE_SAML_AUTHENTICATION)
);

// SCIM管理下では、IdP側でアプリから除外されたユーザー（=ケミカン側で無効化済み）
// のみ削除を許可する。有効なユーザーの削除はIdPの割り当てと矛盾するため止める。
const isDeleteBlockedByScim = (user: User) =>
  isScimEnabled.value && user.is_active;

const handlePasswordReset = (userId: string) => {
  console.log('Password reset initiated for user ID:', userId);
};

const showSendActivationDialog = (user: User) => {
  if (!hasPermission(P.USER_INVITE)) {
    return;
  }
  targetUser.value = { ...user };
  isUserSendActivationDialogVisible.value = true;
};

const showPasswordResetDialog = (user: User) => {
  if (!hasPermission(P.USER_PASSWORD_RESET)) {
    return;
  }
  targetUser.value = { ...user };
  isPasswordResetDialogVisible.value = true;
};

const onUserUpdated = () => {
  emits('updated');
};

const onUserDeleted = async (email: string) => {
  await deleteCompanyUser(email);
  emits('deleted');
};

const isMyAccount = (email: string): boolean => {
  const myEmail = readAssertedUserProfile(store)['email'];
  return myEmail == email;
};

const sendActivationMail = async (email: string) => {
  await regenerateActivationMail({ email });
};

const showDeleteDialog = (user: User) => {
  // If it's the current user's own account, don't show the delete dialog
  if (!hasPermission(P.USER_DELETE) || isMyAccount(user.email)) {
    return;
  }
  isUserDeleteDialogVisible.value = true;
  targetUser.value = { ...user };
};

const showDeactivateDialog = (user: User) => {
  if (!hasPermission(P.USER_DEACTIVATE)) {
    return;
  }
  if (isMyAccount(user.email)) {
    return;
  }
  isUserDeactivateDialogVisible.value = true;
  targetUser.value = { ...user };
};

const onMoreOptionsMenuInput = (email: string, isOpen: boolean) => {
  if (isOpen) {
    openMenuUserEmail.value = email;
    highlightRow.value = email;
    return;
  }

  if (openMenuUserEmail.value === email) {
    openMenuUserEmail.value = '';
  }
};

const handleRowMouseLeave = (email: string) => {
  if (openMenuUserEmail.value === email) {
    return;
  }
  highlightRow.value = '';
};

const onUserDeactivated = async (userUuid: string) => {
  try {
    await deactivateCompanyUser(userUuid);
    emits('updated');
  } catch (error) {
    console.error('ユーザー無効化エラー:', error);
  }
};

const showUserUpdateDialog = (user: User) => {
  if (!hasPermission(P.USER_EDIT)) {
    return;
  }
  isUserUpdateDialogVisible.value = true;
  targetUser.value = { ...user };
};

const formatDeliveryRequestStatus = (
  isAnlaysisEmailDeliveryRequested: boolean
) => {
  return isAnlaysisEmailDeliveryRequested
    ? $t(sharedMessages.subscribe)
    : $t(sharedMessages.unsubscribe);
};

const formatUserLoginAt = (user: User) => {
  if (user.login_at == null) return '';

  return formatToDateTime(user.login_at);
};

return { __sfc: true,$t, m, emits, highlightRow, openMenuUserEmail, isUserDeleteDialogVisible, isUserUpdateDialogVisible, isUserSendActivationDialogVisible, isPasswordResetDialogVisible, isUserDeactivateDialogVisible, targetUser, deleteCompanyUser, deactivateCompanyUser, regenerateActivationMail, hasPermission, P, companyDepartments, departmentPathByUuid, MAX_VISIBLE_DEPARTMENTS, visibleDepartments, hiddenDepartments, hiddenDepartmentCount, COMPANY_FEATURES, isFeatureActive, isScimEnabled, isSamlLoginEnabled, isDeleteBlockedByScim, handlePasswordReset, showSendActivationDialog, showPasswordResetDialog, onUserUpdated, onUserDeleted, isMyAccount, sendActivationMail, showDeleteDialog, showDeactivateDialog, onMoreOptionsMenuInput, handleRowMouseLeave, onUserDeactivated, showUserUpdateDialog, formatDeliveryRequestStatus, formatUserLoginAt, TextTruncatedChip, formatToDate, RoleId, UserUpdateDialog, UserDeleteDialog, UserSendActivationDialog, UserPasswordResetDialog, UserDeactivateDialog, sharedMessages }
}

})